import { useState, useEffect, useRef, useCallback } from "react";
import { Category } from "../types/category.type";
import { SearchResult } from "../interface/search.result.interface";
// import { allResults, categoryIcons, categoryLabels, type Category, type SearchResult } from "../../../data/mockData";


interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const allResults :SearchResult[] = [];

const categoryLabels: Record<Category, string> = {
  posts: "Posts",
  people: "People",
  articles: "Articles",
  products: "Products",
};


const categoryIcons: Record<Category, string> = {
  posts: "📝",
  people: "👥",
  articles: "📰",
  products: "🛍️",
};

const TABS: ("all" | Category)[] = ["all", "posts", "people", "articles", "products"];

const badgeColors: Record<string, string> = {
  Trending: "bg-orange-100 text-orange-600",
  Popular: "bg-pink-100 text-pink-600",
  "Editor's Pick": "bg-indigo-100 text-indigo-600",
  Hot: "bg-red-100 text-red-600",
  "Best Seller": "bg-amber-100 text-amber-700",
  New: "bg-emerald-100 text-emerald-600",
  "Top Rated": "bg-violet-100 text-violet-600",
  Pro: "bg-blue-100 text-blue-600",
  Verified: "bg-teal-100 text-teal-600",
};

function highlight(text: string, query: string) {
  if (!query.trim()) return <span>{text}</span>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function ResultCard({ result, query }: { result: SearchResult; query: string }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors group">
      <img
        src={result.image}
        alt={result.title}
        className="w-11 h-11 rounded-lg object-cover flex-shrink-0 bg-slate-100 border border-slate-200"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
            {highlight(result.title, query)}
          </p>
          {result.badge && (
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                badgeColors[result.badge] ?? "bg-slate-100 text-slate-600"
              }`}
            >
              {result.badge}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
          {highlight(result.subtitle, query)}
        </p>
        {result.meta && (
          <p className="text-[11px] text-slate-400 mt-1">{result.meta}</p>
        )}
      </div>
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | Category>("all");
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const filtered = allResults.filter((r) => {
    const matchesTab = activeTab === "all" || r.category === activeTab;
    const q = query.toLowerCase().trim();
    const matchesQuery =
      !q ||
      r.title.toLowerCase().includes(q) ||
      r.subtitle.toLowerCase().includes(q) ||
      (r.meta ?? "").toLowerCase().includes(q);
    return matchesTab && matchesQuery;
  });

  // Group by category
  const grouped: Partial<Record<Category, SearchResult[]>> = {};
  filtered.forEach((r) => {
    if (!grouped[r.category]) grouped[r.category] = [];
    grouped[r.category]!.push(r);
  });

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setQuery("");
      setActiveTab("all");
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (!isOpen) onClose(); // trigger open from parent
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="w-full max-w-2xl bg-white dark:bg-dark_grey rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        style={{ animation: "modalIn 0.22s cubic-bezier(.4,0,.2,1)" }}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100">
          <svg className="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
            <circle cx="11" cy="11" r="8" />
            <path strokeLinecap="round" d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search posts, people, articles, products…"
            className="flex-1 text-sm text-slate-800 placeholder-slate-400 bg-transparent outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-400 text-[10px] font-mono rounded-md border border-slate-200 flex-shrink-0">
            ESC
          </kbd>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 py-2.5 border-b border-slate-100 overflow-x-auto scrollbar-hide">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                activeTab === tab
                  ? "bg-tealGreen dark:bg-primary dark:text-midnight_text text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              }`}
            >
              {tab !== "all" && <span>{categoryIcons[tab as Category]}</span>}
              {tab === "all" ? "All" : categoryLabels[tab as Category]}
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === tab ? "bg-white/20 text-white dark:text-midnight_text" : "bg-slateGray text-muted"
                }`}
              >
                {tab === "all"
                  ? allResults.filter((r) => {
                      const q = query.toLowerCase().trim();
                      return (
                        !q ||
                        r.title.toLowerCase().includes(q) ||
                        r.subtitle.toLowerCase().includes(q)
                      );
                    }).length
                  : allResults.filter((r) => {
                      const q = query.toLowerCase().trim();
                      return (
                        r.category === tab &&
                        (!q ||
                          r.title.toLowerCase().includes(q) ||
                          r.subtitle.toLowerCase().includes(q))
                      );
                    }).length}
              </span>
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="dark:bg-deepSlate bg-charcoalGray/30 overflow-y-auto max-h-[55vh] py-2 ">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <circle cx="11" cy="11" r="8" />
                  <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                </svg>
              </div>
              <p className="text-slate-700 font-semibold text-sm">No results found</p>
              <p className="text-slate-400 text-xs mt-1">
                Try a different keyword or browse all categories.
              </p>
            </div>
          ) : activeTab === "all" ? (
            // Grouped view
            (Object.keys(grouped) as Category[]).map((cat) => (
              <div key={cat} className="mb-2 border-b border-b-midnight_text/50 dark:border-b-border/50">
                <div className="flex items-center gap-2 px-4 py-1.5">
                  <span className="text-base">{categoryIcons[cat]}</span>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    {categoryLabels[cat]}
                  </span>
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-[10px] text-slate-300 font-semibold">{grouped[cat]!.length}</span>
                </div>
                {grouped[cat]!.map((r) => (
                  <ResultCard key={r.id} result={r} query={query} />
                ))}
              </div>
            ))
          ) : (
            // Flat view for single category
            <div>
              {filtered.map((r) => (
                <ResultCard key={r.id} result={r} query={query} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <kbd className="px-1.5 py-0.5 bg-white dark:text-midnight_text border border-slate-200 rounded text-[10px] font-mono shadow-sm">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <kbd className="px-1.5 py-0.5 bg-white dark:text-midnight_text border border-slate-200 rounded text-[10px] font-mono shadow-sm">↵</kbd>
              Open
            </span>
          </div>
          <span className="text-[11px] text-slate-400">
            {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>
    </div>
  );
}
